/* ============================================================
   Flow Wake-Up Quiz — Content (v4 — Figma-aligned)
   Exposes: window.QUIZ, window.ARCHETYPES, window.demoDisplay

   Sequence matches the actual Figma funnel order (left-to-right
   on the canvas of the "[Site] Quiz Funnel" page):

     landing
       → wake_time
       → alarm_count → late_frequency → snooze_count
       → interstitial: snooze adds up           (Figma 6)
       → first_morning → phone_time             (Figma 7-8)
       → interstitial: it's a cycle             (Figma 10)
       → brain_fog → tired_unmotivated          (Figma 11-12)
       → interstitial: you're not lazy          (Figma 13)
       → unlock_what → good_morning             (Figma 14-15)
       → interstitial: best version timeline    (Figma 17-18)
       → calibration → email gate
       → scratch (reveals Digital Detox Guide)
       → dock showcase (Flow Dock image)
       → redirect to LP

   Archetype-personalization (Snoozer/Hostage/Inconsistent/Steady)
   is computed silently from question scores and passed to the LP
   via ?match=… — it's not shown in the quiz itself.
   ============================================================ */

(function () {
  var R = { SNOOZER: 'snoozer', HOSTAGE: 'hostage', INCONSISTENT: 'inconsistent' };
  function s(snoozer, hostage, inconsistent) {
    return { snoozer: snoozer || 0, hostage: hostage || 0, inconsistent: inconsistent || 0 };
  }

  var LOW_SIGNAL_THRESHOLD = 14;

  // ----------------------------------------------------------
  // Archetypes (unchanged copy — used by the LP)
  // ----------------------------------------------------------
  var ARCHETYPES = {
    snoozer: {
      key: 'snoozer',
      name: 'The Habitual Snoozer',
      tagline: "You don't lack willpower at 7am. You lack a body that's moved out of bed yet.",
      revealKicker: 'Your Morning Archetype',
      revealDemoLine: 'The snooze loop peaks in {gender} {ageBucket} — and the longer it runs, the harder it gets to break.',
      revealDiagnosis: "You're stacking alarms and treating snooze like a feature. Every loop costs you 20–60 minutes of usable morning, and the longer it goes the harder your day is to recover. The fix isn't more discipline — it's making 'stay in bed' physically impossible.",
      revealBullets: [
        { icon: '⏰', title: 'Snooze loop',     subtitle: "Most mornings, you hit it at least once",   accent: '20–60 min lost' },
        { icon: '🔔', title: 'Backup alarms',   subtitle: "One isn't enough — you stack 3+" },
        { icon: '🛏️', title: "Arm's reach",     subtitle: "Your alarm lives where snoozing wins" }
      ],
      lp: {
        kicker: 'YOUR MORNING ARCHETYPE',
        heroTagline: "You don't lack willpower at 7am. You lack a body that's moved out of bed yet.",
        heroBody: "Snoozing isn't a discipline problem — it's a physics problem. As long as your alarm is within arm's reach, your half-asleep brain will always vote to stay. Flow fixes that by making the alarm unreachable from the bed.",
        pattern: {
          title: "Here's what's actually happening at 7am",
          rows: [
            "Your alarm rings. <b>Your half-awake brain</b> reaches for the closest button.",
            "You hit snooze. <b>That feels harmless</b> — but it just bought you 9 more minutes of fragmented sleep that won't recover anything.",
            "Loop runs 3–6 times. <b>You lose 30–60 minutes</b>, wake up groggier than the first alarm, and start the day already behind."
          ]
        },
        why: {
          title: 'Why Flow ends the snooze loop',
          features: [
            { icon: '⏰', title: "No snooze. None.", body: "There's literally no snooze button. The alarm keeps ringing until you walk to the Dock and scan it." },
            { icon: '🚶', title: "Your feet hit the floor.", body: "The Dock lives across the room. By the time you've scanned it, you're already standing, already awake." },
            { icon: '📈', title: "Tracks your wins.", body: "See your wake streak grow. The first 5-day streak is the moment your mornings actually change." }
          ]
        },
        testimonial: {
          quote: "Flow's an alarm clock that forces you out of bed. This has helped me so much. I now go to the gym every single day. I don't snooze my alarm, I physically can't.",
          author: 'Hezekiah S.',
          verified: 'Verified buyer'
        },
        ctaTitle: "Your snooze loop has lasted long enough.",
        ctaSub: "Tomorrow morning could be different. Flow gets to your door in days."
      }
    },

    hostage: {
      key: 'hostage',
      name: 'The Phone Hostage',
      tagline: "Your alarm works. Your phone won't let go.",
      revealKicker: 'Your Morning Archetype',
      revealDemoLine: 'The bed-phone trap shows up most in {gender} {ageBucket}. You\'re wired exactly the same way the algorithm wants you to be.',
      revealDiagnosis: "You're not snoozing — you turned off the alarm in 3 seconds. The problem comes after. Phone in hand, 30 minutes vanish to scroll, and the day starts in the worst possible mental state: reactive, distracted, behind. The fix is getting your phone *out* of the bedroom equation in the morning.",
      revealBullets: [
        { icon: '📱', title: 'Phone first',     subtitle: 'Your first physical action is reaching for it', accent: '25 min trap' },
        { icon: '🛏️', title: 'Awake in bed',    subtitle: 'Up within minutes, but in bed for 20–60 more' },
        { icon: '📰', title: 'Feed-first day',  subtitle: "Your morning starts in a feed, not your life" }
      ],
      lp: {
        kicker: 'YOUR MORNING ARCHETYPE',
        heroTagline: "Your alarm works. Your phone won't let go.",
        heroBody: "The problem isn't waking up — you do that fine. It's the 30 minutes after, with the phone in your hand, that eats your morning. Flow makes your phone the tool you use to *leave* the bed, not the trap that keeps you in it.",
        pattern: {
          title: "Here's what's actually happening at 7am",
          rows: [
            "Alarm rings. <b>You dismiss it in 3 seconds.</b> Easy. You're 'awake'.",
            "<b>Phone stays in hand.</b> One quick check. Feed. Reply. Another feed. 25 minutes gone.",
            "<b>You stand up panicked.</b> Day starts late, reactive, in someone else's priorities."
          ]
        },
        why: {
          title: 'Why Flow ends the bed-phone trap',
          features: [
            { icon: '🚪', title: 'Phone leaves the bed.', body: "Walk to the Dock to dismiss the alarm. Your phone follows you out — into the day, not the feed." },
            { icon: '🔒', title: 'Block the apps you scroll.', body: "Choose which apps stay locked until you scan the Dock. Instagram, TikTok, X — they don't open from bed." },
            { icon: '☀️', title: 'Lock Mode for the morning hour.', body: "One tap locks every distraction for a 60-minute deep-start. Your first hour is yours again." }
          ]
        },
        testimonial: {
          quote: "It really changed my life. I don't miss anything anymore, I'm actually on time, and life feels ten times better. It's the one thing you actually can't snooze.",
          author: 'Joseph M.',
          verified: 'Verified buyer'
        },
        ctaTitle: "Get your phone — and your morning — back.",
        ctaSub: "Tomorrow's first hour can belong to you, not your feed. Flow ships in days."
      }
    },

    inconsistent: {
      key: 'inconsistent',
      name: 'The Inconsistent Riser',
      tagline: "On good days you're up at 6. On bad days the day is over before it starts.",
      revealKicker: 'Your Morning Archetype',
      revealDemoLine: 'Inconsistent wake times are most common in {gender} {ageBucket} — especially when weekday and weekend schedules pull in different directions.',
      revealDiagnosis: "Your wake-up time is mood-driven, not schedule-driven. A great Tuesday and a wrecked Wednesday read like two different people. Inconsistent wake times are the single biggest driver of bad sleep, low energy, and missed mornings — and they're the easiest to fix once the wake-up itself isn't optional anymore.",
      revealBullets: [
        { icon: '🌀', title: 'Wake time swings',  subtitle: '1–3 hour spread week to week',          accent: 'mood-driven' },
        { icon: '🎭', title: 'Mood decides',      subtitle: "Whether you get up depends on the day" },
        { icon: '⚖️', title: 'Best vs worst',     subtitle: 'Best mornings feel great. Worst feel impossible' }
      ],
      lp: {
        kicker: 'YOUR MORNING ARCHETYPE',
        heroTagline: "On good days you're up at 6. On bad days the day is over before it starts.",
        heroBody: "Mood is a terrible alarm clock. When getting up is optional, your brain will negotiate it down on every difficult morning — and the difficult ones are the days you need the win most. Flow makes the wake-up non-negotiable, so the swings stop.",
        pattern: {
          title: "Here's what's actually happening at 7am",
          rows: [
            "<b>Some mornings: easy.</b> You're up at 6, ready, productive. Those days feel great.",
            "<b>Other mornings: impossible.</b> The alarm becomes a suggestion. You negotiate. You lose two hours.",
            "<b>Net result: you can't plan around yourself.</b> Habits don't stick. Sleep stays bad. Mornings stay random."
          ]
        },
        why: {
          title: 'Why Flow stabilizes your wake-up',
          features: [
            { icon: '🧱', title: 'Wake-up is no longer optional.', body: "The alarm rings until you walk to the Dock. Mood can't vote. Every morning gets the same start." },
            { icon: '📊', title: 'Sleep tracking, every night.', body: "See the actual pattern. Stages, duration, trends — so the next change you make is informed, not guessed." },
            { icon: '📅', title: 'Schedules that lock themselves.', body: "Set a recurring wake-up + focus block. Flow enforces it, even on the mornings you want to skip." }
          ]
        },
        testimonial: {
          quote: "Flow has literally changed my life for the better. Every morning is easier because I physically have to get up to turn off my alarm, making it almost impossible to fall back asleep.",
          author: 'Sarah K.',
          verified: 'Verified buyer'
        },
        ctaTitle: "Stop letting mood pick your wake-up time.",
        ctaSub: "A consistent morning is one purchase away. Flow ships in days."
      }
    },

    steady: {
      key: 'steady',
      name: 'The Steady Riser',
      tagline: "Your mornings already work. The real risk is the week that breaks them.",
      revealKicker: 'Your Morning Archetype',
      revealDemoLine: 'Most {gender} {ageBucket} struggle with at least one wake-up pattern. You don\'t — and that\'s rarer than you think.',
      revealDiagnosis: "You're in the rare category — your mornings are largely working. You're up on time, your phone isn't dragging you down, and you've built something real. The problem you face isn't fixing a broken pattern — it's the Tuesday three weeks from now when sleep is bad, you're stressed, and the routine that took you years to build collapses in a week. Flow is the safety net that makes the routine unbreakable.",
      revealBullets: [
        { icon: '🎯', title: 'Consistent wake',  subtitle: 'Same time, day after day',              accent: 'top 8% of users' },
        { icon: '🚪', title: 'Phone stays out',  subtitle: "It isn't dragging your morning down" },
        { icon: '🛡️', title: 'Worth protecting', subtitle: "Your habit is real — keep it that way" }
      ],
      lp: {
        kicker: 'YOUR MORNING ARCHETYPE',
        heroTagline: "Your mornings already work. The real risk is the week that breaks them.",
        heroBody: "You scored low on every failure mode we tested — consistent wake times, no phone-in-bed trap, no snooze loop. That's rare. The next question isn't how to fix mornings; it's how to make sure no single bad week ever erases what you've built.",
        pattern: {
          title: "What you're actually protecting",
          rows: [
            "<b>You've already won the hardest part.</b> Showing up on time, day after day, is something most people never do.",
            "<b>But every consistent routine has a failure mode.</b> Sleep gets bad. Work gets stressful. One Tuesday turns into a week of slipping.",
            "<b>Habits don't die because someone stops trying.</b> They die because the friction creeps back in. Flow keeps the friction out."
          ]
        },
        why: {
          title: 'Why Flow protects what you\'ve built',
          features: [
            { icon: '🛡️', title: "Physical-by-design.", body: "Even on the hardest mornings — sick, jet-lagged, stressed — the wake-up still happens. Mood can't vote." },
            { icon: '📈', title: "Sleep tracking as an early warning.", body: "Recovery drops before the routine does. See the trend coming so you can adjust before a streak breaks." },
            { icon: '🔒', title: "Protect your focus block.", body: "Schedules lock distractions automatically. The hour you've protected for yourself stays protected." }
          ]
        },
        testimonial: {
          quote: "I already had a solid morning routine. Flow took it from 'most days' to 'every day,' even when I'm wrecked. It's the difference between a streak that breaks and one that compounds.",
          author: 'Customer story',
          verified: 'Verified buyer'
        },
        ctaTitle: "Protect what you've built.",
        ctaSub: "Your routine is rare. Flow makes sure no bad week erases it."
      }
    }
  };

  // ----------------------------------------------------------
  // QUESTIONS (Figma-aligned)
  // Questions (all single-select).
  // ----------------------------------------------------------
  var QUESTIONS = [
    // Wake time — drives the personalized "morning with Flow" timeline.
    // `base` is minutes-from-midnight; the timeline adds per-row offsets.
    {
      id: 'wake_time',
      type: 'single',
      title: 'What time do you usually wake up?',
      options: [
        { label: 'Before 6 AM', base: 345, scores: s(0, 0, 0) }, // 5:45
        { label: '6 AM',        base: 360, scores: s(0, 0, 0) },
        { label: '7 AM',        base: 420, scores: s(0, 0, 0) },
        { label: '8 AM',        base: 480, scores: s(0, 0, 0) },
        { label: '9 AM',        base: 540, scores: s(0, 0, 0) },
        { label: 'After 9 AM',  base: 585, scores: s(0, 0, 0) }  // 9:45
      ]
    },

    // Alarm / snooze cluster (Figma 3-5)
    {
      id: 'alarm_count',
      type: 'single',
      title: 'How many alarms do you set to wake up?',
      options: [
        { label: '1',  scores: s(0, 1, 0) },
        { label: '2',  scores: s(1, 1, 0) },
        { label: '3',  scores: s(2, 1, 1) },
        { label: '4',  scores: s(3, 1, 1) },
        { label: '5+', scores: s(5, 1, 2) }
      ]
    },
    {
      id: 'late_frequency',
      type: 'single',
      title: 'How often are you late because you slept through your alarm?',
      options: [
        { label: 'Never',                   scores: s(0, 0, 0) },
        { label: 'Once a month',            scores: s(1, 0, 1) },
        { label: 'Weekly',                  scores: s(3, 1, 2) },
        { label: 'Multiple times a week',   scores: s(5, 1, 3) }
      ]
    },
    {
      id: 'snooze_count',
      type: 'single',
      title: 'How many times do you hit snooze before getting up?',
      options: [
        { label: '0',  scores: s(0, 0, 0) },
        { label: '1',  scores: s(1, 0, 1) },
        { label: '2',  scores: s(2, 0, 1) },
        { label: '3',  scores: s(4, 1, 2) },
        { label: '4+', scores: s(5, 1, 2) }
      ]
    },

    // Morning / phone cluster (Figma 7-8)
    {
      id: 'first_morning',
      type: 'single',
      title: "What's the first thing you do in the morning?",
      options: [
        { label: 'Check my phone',      scores: s(0, 4, 1) },
        { label: 'Get straight up',     scores: s(0, 0, 0) },
        { label: 'Lay in bed thinking', scores: s(2, 1, 2) }
      ]
    },
    {
      id: 'phone_time',
      type: 'slider',
      title: 'How much time do you spend on your phone before getting out of bed?',
      min: 0,
      max: 60,
      step: 1,
      defaultValue: 20,
      options: [
        { label: '< 10 mins',  min: 0,  max: 9,  scores: s(0, 0, 0) },
        { label: '10–29 mins', min: 10, max: 29, scores: s(1, 3, 1) },
        { label: '30–59 mins', min: 30, max: 59, scores: s(1, 5, 2) },
        { label: '60+ mins',   min: 60,          scores: s(2, 5, 3) }
      ]
    },

    // Symptom cluster (Figma 11-12)
    {
      id: 'brain_fog',
      type: 'single',
      title: "Do you have days where you have brain fog and can't think clearly?",
      options: [
        { label: 'Never',             scores: s(0, 0, 0) },
        { label: 'Rarely',            scores: s(0, 0, 1) },
        { label: 'Sometimes',         scores: s(1, 1, 2) },
        { label: 'Yeah, all the time', scores: s(2, 2, 3) }
      ]
    },
    {
      id: 'tired_unmotivated',
      type: 'single',
      title: 'Do you have days where you feel unmotivated and tired?',
      options: [
        { label: 'Not really',  scores: s(0, 0, 0) },
        { label: 'Sometimes',   scores: s(1, 1, 1) },
        { label: 'Most days',   scores: s(2, 2, 3) },
        { label: 'Constantly',  scores: s(3, 2, 4) }
      ]
    },

    // Aspiration (Figma 14-15)
    {
      id: 'unlock_what',
      type: 'single',
      title: 'What would waking up on time unlock for you?',
      options: [
        { label: 'More time for myself',         scores: s(1, 1, 1) },
        { label: 'Hitting the gym',              scores: s(1, 0, 1) },
        { label: 'Getting ahead at work/school', scores: s(0, 2, 0) },
        { label: 'Just feeling better',          scores: s(1, 1, 2) }
      ]
    },
    {
      id: 'good_morning',
      type: 'single',
      title: 'What does a good morning look like to you?',
      options: [
        { label: 'Calm and slow',          scores: s(0, 1, 1) },
        { label: 'Productive and focused', scores: s(0, 2, 0) },
        { label: 'Active and energized',   scores: s(1, 0, 1) }
      ]
    }
  ];

  // ----------------------------------------------------------
  // Interstitials
  // ----------------------------------------------------------
  var INTERSTITIALS = [
    {
      id: 'inter_snooze_not_alone',
      type: 'interstitial',
      variant: 'answerStat',
      answerFrom: 'snooze_count',
      title: "You're not alone.",
      stats: [
        { percent: '18%', answerLabel: '0' },
        { percent: '19%', answerLabel: '1' },
        { percent: '18%', answerLabel: '2' },
        { percent: '29%', answerLabel: '3' },
        { percent: '16%', answerLabel: '4+' }
      ],
      fallback: {
        percent: '66%',
        body: 'of people say snooze is part of their morning.'
      },
      cta: 'Continue'
    },

    {
      id: 'inter_snooze_biology',
      type: 'interstitial',
      variant: 'graph',
      title: 'This is not your fault.',
      graph: 'sleepCycle',
      footer: [
        { text: 'Snoozing pulls you back toward sleep.', dim: true },
        { text: 'The next alarm jolts you out mid-cycle.', dim: true },
        {
          parts: [
            { text: 'That can leave you ' },
            { text: 'foggy for hours', accent: true, underline: true },
            { text: '.' }
          ],
          dim: false
        }
      ],
      cta: 'Continue'
    },

    // 1. "That snoozing adds up." — Figma final (11888:8476). Gradient card
    // with Week / Month / Year horizontal bars (~45 min/day): 315 min →
    // 23 hrs → 274 hrs. The 274 hrs/year matches the "274 hours" wasted
    // screen so the two figures stay consistent. Year is a full solid-red
    // bar. The old per-day stat-card version lives in wakeup-backup-20260529.
    {
      id: 'inter_snooze_stat',
      type: 'interstitial',
      variant: 'barChart',
      title: 'That snoozing adds up.',
      subtitlePre: 'If you snooze ',
      subtitleEm: '3 times',
      subtitlePost: " a day, here's what you've lost.",
      bars: [
        { label: 'Week',  value: '315 min', fill: 22, fillColor: 'rgba(223, 60, 60, 0.25)' },
        { label: 'Month', value: '23 hrs',  fill: 48, fillColor: 'rgba(223, 60, 60, 0.5)'  },
        { label: 'Year',  value: '274 hrs', full: true }
      ],
      cta: 'Continue'
    },

    // 2. "It's a cycle." — Figma v1.1 (11909:9941). Ring of 4 nodes; the
    // top ("Poor sleep") and right ("Snooze 5x") carry an orange border and
    // the arc between them is a dashed orange arrow. Center is orange.
    {
      id: 'inter_cycle',
      type: 'interstitial',
      variant: 'cycle',
      title: "It's a cycle.",
      body: "And it spins faster every day you don't fix it.",
      nodes: [
        { label: 'Poor sleep',         position: 'top' },
        { label: 'Snooze 5x',          position: 'right' },
        { label: 'Drained by noon',    position: 'bottom' },
        { label: 'Wired, up too late', position: 'left' }
      ],
      center: 'You can break it.',
      cta: 'Continue'
    },

    // 3. "You're not lazy. That's how your body works." — Figma v1.1
    // (11907:9834). Sleep-cycle line graph exported @3x from Figma, with
    // a 3-line footer (first two dim, last line white).
    {
      id: 'inter_youre_not_lazy',
      type: 'interstitial',
      variant: 'graph',
      title: "You're not lazy.\nThat's how your body works.",
      graph: 'sleepCycle',
      footer: [
        { text: 'Snoozing drops you back into deep sleep.', dim: true },
        { text: 'The 2nd alarm rips you out mid-cycle.', dim: true },
        { text: 'That fog lasts hours.', dim: false }
      ],
      cta: 'Continue'
    },

    // 4. Computed annual phone-time loss. Stark text treatment, no stock photo.
    {
      id: 'inter_days_gone',
      type: 'interstitial',
      variant: 'graph',
      cta: 'Continue'
    },

    // 5. "This is your turnaround." — Figma v1.1 (11901:9377). Ascending
    // 10%→100% line graph (exported @3x) + 3 benefit rows (exported @3x).
    {
      id: 'inter_you_vs_flow',
      type: 'interstitial',
      variant: 'graph',
      title: 'This is your turnaround.',
      subtitle: 'Where you are now, and where Flow takes you.',
      graph: 'turnaround',
      cta: 'Continue'
    },

    // First product-demo beat. Personalizes the wake time from the user's
    // wake_time answer and advances by tapping anywhere on the screen.
    {
      id: 'inter_demo_alarm_rings',
      type: 'interstitial',
      variant: 'flowDemoAlarm',
      baseFrom: 'wake_time',
      baseDefault: 480,
      image: 'flow-alarm-rings-demo-8am.png',
      // Keyed by wake_time option `base` (minutes-from-midnight). Renderer
      // looks up imageMap[wakeBase] and falls back to `image` above.
      imageMap: {
        345: 'flow-alarm-rings-demo-545am.png',
        360: 'flow-alarm-rings-demo-6am.png',
        420: 'flow-alarm-rings-demo-7am.png',
        480: 'flow-alarm-rings-demo-8am.png',
        540: 'flow-alarm-rings-demo-9am.png',
        585: 'flow-alarm-rings-demo-945am.png'
      },
      cta: 'tap to continue'
    },

    {
      id: 'inter_demo_only_one_way',
      type: 'interstitial',
      variant: 'situationIntro',
      eyebrow: "HERE'S HOW FLOW WORKS",
      title: [
        { text: "There's " },
        { text: "only one way", accent: true },
        { text: " to turn off the alarm..." }
      ]
    },

    {
      id: 'inter_demo_walk_to_dock',
      type: 'interstitial',
      variant: 'flowDemoAlarm',
      image: 'flow-dock-scan-demo-8am.png',
      imageMap: {
        345: 'flow-dock-scan-demo-545am.png',
        360: 'flow-dock-scan-demo-6am.png',
        420: 'flow-dock-scan-demo-7am.png',
        480: 'flow-dock-scan-demo-8am.png',
        540: 'flow-dock-scan-demo-9am.png',
        585: 'flow-dock-scan-demo-945am.png'
      },
      eyebrow: "HERE'S HOW FLOW WORKS",
      title: [
        { text: 'You ' },
        { text: 'get up', accent: true },
        { text: ' and walk to your dock.' }
      ],
      subtitle: 'It can be in your bathroom, next to your coffee maker, or even in your pool.',
      cta: 'tap to continue'
    },

    {
      id: 'inter_demo_awake_ready',
      type: 'interstitial',
      variant: 'situationIntro',
      title: "Now you're awake and ready to conquer the day.",
      visual: 'sunrise'
    },

    {
      id: 'inter_alarm_sounds',
      type: 'interstitial',
      variant: 'alarmSounds',
      title: 'Choose from 100+ alarm sounds.',
      sectionLabel: 'Popular',
      sounds: [
        { title: 'Wakey Wakey', category: 'Unhinged', src: 'alarm-preview-wakey-wakey.mp3' },
        { title: 'F*** Yourself', category: 'Motivational', src: 'alarm-preview-f-yourself.mp3' },
        { title: 'Let Them Doubt You', category: 'Motivational', src: 'alarm-preview-let-them-doubt-you.mp3' },
        { title: 'Gentle Rise', category: 'Gentle Rise', src: 'alarm-preview-gentle-rise.mp3' },
        { title: 'First Light', category: 'Gentle Rise', src: 'alarm-preview-first-light.mp3' }
      ],
      cta: 'Continue'
    },

    {
      id: 'inter_sleep_reports',
      type: 'interstitial',
      variant: 'image',
      title: 'Improve your sleep.',
      subtitle: 'Sleep reports every night.',
      image: 'sleep-reports-demo.png',
      className: 'image-interstitial--sleep-reports',
      cta: 'Continue'
    },

    {
      id: 'inter_energy_potential',
      type: 'interstitial',
      variant: 'graph',
      title: 'Time to start.',
      subtitle: 'Wake up with the energy to become who you know you can be.',
      graph: 'potential',
      cta: 'Continue'
    },

    {
      id: 'inter_commitment_final',
      type: 'interstitial',
      variant: 'commitment',
      title: [
        { text: "This is " },
        { text: "just text on a screen", accent: true },
        { text: ".\n\nIt can't convince you to do anything.\n\nYou have to dig deep into " },
        { text: "what you truly want", accent: true },
        { text: ".\n\n" },
        { text: "Don't stall", accent: true },
        { text: ".\n\nMake the decision to " },
        { text: "change your life right now", accent: true },
        { text: "." }
      ],
      cta: 'Commit',
      href: 'https://flowalarmclock.com/products/flow-alarm'
    },

    // 6. "This is what a morning with Flow looks like" (Figma 11821:14932)
    // Real animated timeline. Green (#08cb56) connecting line with check
    // dots, paired with time + description rows on the right.
    {
      id: 'inter_morning_with_flow',
      type: 'interstitial',
      variant: 'morningWithFlow',
      title: 'This is what a morning with Flow looks like.',
      // Times are computed from the user's wake_time answer (base) plus
      // each row's minute offset, so the timeline reflects their morning.
      baseFrom: 'wake_time',
      baseDefault: 390, // 6:30 AM fallback if wake_time is unanswered
      rows: [
        { offset: 0, text: 'Alarm rings. No snooze option.' },
        { offset: 1, text: 'Complete a mission. Alarm is off.' },
        { offset: 2, text: 'Apps stay blocked for 30 mins.' },
        { offset: 7, text: 'Your morning starts. Exercise, read, breathe.' }
      ],
      cta: 'Continue'
    },

    // 7. "It's time to become the best version of yourself." (Figma 11821:14996)
    // Hero card uses ORANGE (alarm icon + "6:30 AM" + "You're up. First try."),
    // followed by 3 benefit cards with orange icon + white title + dim subtitle.
    // (Green dots are on the "Morning with Flow" interstitial, not this one.)
    {
      id: 'inter_best_version',
      type: 'interstitial',
      variant: 'bestVersion',
      titleLine1: "It's time to become the best version of yourself.",
      titleLine2: "Stop saying you'll start tomorrow.",
      // Hero time is personalized from the wake_time answer (base), like the
      // morning-with-Flow timeline. hero.time is the fallback.
      baseFrom: 'wake_time',
      baseDefault: 390,
      hero: {
        icon: '⏰',
        time: '6:30 AM',
        result: "You're up. First try."
      },
      cards: [
        { icon: '🕐', title: 'No rushing',  subtitleFromWake: true },
        { icon: '💪', title: 'Exercising',  subtitle: 'Start the day already winning.' },
        { icon: '📖', title: 'Reading',     subtitle: 'Even 10 min before the day starts will make a difference.' }
      ],
      cta: 'Continue'
    }
  ];

  // ----------------------------------------------------------
  // Sequence (matches Figma order)
  // ----------------------------------------------------------
  var SEQUENCE = [
    { kind: 'landing' },
    { kind: 'question', id: 'wake_time' },   // personalizes the morning-with-Flow timeline
    // Alarm / snooze cluster (Figma 3-5)
    { kind: 'question', id: 'alarm_count' },
    { kind: 'question', id: 'late_frequency' },
    { kind: 'question', id: 'snooze_count' },
    { kind: 'interstitial', id: 'inter_snooze_not_alone' },
    // Symptom cluster follows snooze directly.
    { kind: 'question', id: 'brain_fog' },
    { kind: 'question', id: 'tired_unmotivated' },
    { kind: 'interstitial', id: 'inter_snooze_biology' },
    { kind: 'interstitial', id: 'inter_cycle' },              // Figma 10: It's a cycle
    // Morning / phone cluster (Figma 7-8)
    { kind: 'question', id: 'first_morning' },
    { kind: 'question', id: 'phone_time' },
    { kind: 'interstitial', id: 'inter_days_gone' },          // Figma 9: That's X days a year. Gone.
    // Aspiration (Figma 14-15)
    { kind: 'question', id: 'unlock_what' },
    { kind: 'question', id: 'good_morning' },
    { kind: 'interstitial', id: 'inter_you_vs_flow' },        // Figma 16: You now vs You with Flow
    { kind: 'interstitial', id: 'inter_demo_alarm_rings' },   // Demo 1: Your alarm rings
    { kind: 'interstitial', id: 'inter_demo_only_one_way' },  // Demo 2: Only one way to turn it off
    { kind: 'interstitial', id: 'inter_demo_walk_to_dock' },  // Demo 3: Walk to the Dock
    { kind: 'interstitial', id: 'inter_demo_awake_ready' },
    { kind: 'interstitial', id: 'inter_alarm_sounds' },
    { kind: 'interstitial', id: 'inter_sleep_reports' },
    { kind: 'interstitial', id: 'inter_energy_potential' },
    { kind: 'interstitial', id: 'inter_commitment_final' }
  ];

  // ----------------------------------------------------------
  // Combine for lookup
  // ----------------------------------------------------------
  var ALL = QUESTIONS.concat(INTERSTITIALS);
  var QUESTION_BY_ID = {};
  ALL.forEach(function (q) { QUESTION_BY_ID[q.id] = q; });

  window.QUIZ = {
    questions: QUESTIONS,
    interstitials: INTERSTITIALS,
    questionById: QUESTION_BY_ID,
    sequence: SEQUENCE,
    R: R,
    lowSignalThreshold: LOW_SIGNAL_THRESHOLD,
    countableQuestions: SEQUENCE.filter(function (s) { return s.kind === 'question'; }).length
  };

  window.ARCHETYPES = ARCHETYPES;

  // ----------------------------------------------------------
  // Demo display helpers (used by calibration + reveal copy)
  // ----------------------------------------------------------
  var AGE_DISPLAY    = ['under 25', '25–34', '35–44', '45+'];
  var GENDER_DISPLAY = ['men', 'women'];

  function demoDisplay(answers) {
    if (!answers) return { ageBucket: '', gender: '', hasDemo: false };
    var aIdx = typeof answers.age === 'number' ? answers.age : null;
    var gIdx = typeof answers.gender === 'number' ? answers.gender : null;
    var ageBucket = (aIdx != null && AGE_DISPLAY[aIdx]) || '';
    var gender    = (gIdx != null && GENDER_DISPLAY[gIdx]) || '';
    return { ageBucket: ageBucket, gender: gender, hasDemo: !!(ageBucket && gender) };
  }

  function demoInterpolate(template, demo) {
    if (!template) return '';
    return template
      .replace('{ageBucket}', demo.ageBucket || '')
      .replace('{gender}', demo.gender || '');
  }

  window.demoDisplay = demoDisplay;
  window.demoInterpolate = demoInterpolate;
})();
